import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Helmet } from "react-helmet-async";
import {
    FaArrowLeft,
    FaArrowRight,
    FaCalendarAlt,
    FaCheck,
    FaChevronRight,
    FaFire,
    FaHome,
    FaInfoCircle,
    FaMapMarkerAlt,
    FaShieldAlt,
    FaTag,
    FaUsers,
    FaBed,
    FaExpand,
    FaTimes,
    FaCheckCircle,
    FaUser,
} from "react-icons/fa";

const ROOM_IMG = "https://uaediscountedhotels.com/uploads/rooms";

// ============================================================
// DESIGN COLORS
// ============================================================
// Primary Green: #0B6E8E
// Dark Green:    #002B45
// Page Cream:    #F3F7FA
// Soft Green:    #E8F4F7
// Gold:          #F7B928
// ============================================================

export default function RoomDetail() {
    const { roomId } = useParams();
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [room, setRoom] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const [activeImage, setActiveImage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalIndex, setModalIndex] = useState(0);
    const [showBookingModal, setShowBookingModal] = useState(false);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [checkIn, setCheckIn] = useState("");
    const [checkOut, setCheckOut] = useState("");
    const [guests, setGuests] = useState(1);
    const [bookingLoading, setBookingLoading] = useState(false);

    const today = new Date().toISOString().split("T")[0];
    const selectedDate = checkIn || today;

    useEffect(() => {
        document.body.style.overflow =
            showBookingModal || showModal ? "hidden" : "auto";

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [showBookingModal, showModal]);

    useEffect(() => {
        const fetchRoom = async () => {
            try {
                setLoading(true);
                setError(false);

                const res = await axios.get(
                    `https://cheapdubaihotels.com/api/rooms/${roomId}`,
                );

                setRoom(res.data);
                setActiveImage(res.data?.image || null);
            } catch (err) {
                console.error(err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        if (roomId) fetchRoom();
    }, [roomId]);

    // ============================================================
    // LOADING
    // ============================================================

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F3F7FA]">
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#0B6E8E]/15 border-t-[#0B6E8E]" />

                    <p className="mt-4 text-sm font-medium text-slate-500">
                        Loading room details...
                    </p>
                </div>
            </div>
        );
    }

    // ============================================================
    // ERROR
    // ============================================================

    if (error || !room) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F3F7FA] px-4">
                <div className="w-full max-w-md rounded-[28px] border border-[#0B6E8E]/10 bg-white p-8 text-center shadow-[0_20px_60px_rgba(0,43,69,0.10)]">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500">
                        <FaInfoCircle className="text-2xl" />
                    </div>

                    <h2 className="mt-5 text-2xl font-bold text-[#002B45]">
                        Room not found
                    </h2>

                    <p className="mt-2 text-sm leading-6 text-slate-500">
                        We could not load this room. Please return to the previous page.
                    </p>

                    <button
                        onClick={() => navigate(-1)}
                        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#0B6E8E] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#002B45]"
                    >
                        <FaArrowLeft />
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    // ============================================================
    // PARSE IMAGES
    // ============================================================

    let images = [];

    try {
        const parsed = room.images
            ? typeof room.images === "string"
                ? JSON.parse(room.images)
                : room.images
            : [];

        images = Array.isArray(parsed) ? parsed : [];
    } catch {
        images = [];
    }

    // ============================================================
    // PARSE AMENITIES
    // ============================================================

    let amenities = [];

    try {
        const parsed = room.amenities
            ? typeof room.amenities === "string"
                ? JSON.parse(room.amenities)
                : room.amenities
            : [];

        amenities = Array.isArray(parsed)
            ? parsed
            : typeof parsed === "string"
                ? parsed
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean)
                : [];
    } catch {
        amenities =
            typeof room.amenities === "string"
                ? room.amenities
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean)
                : [];
    }

    const allImages = [...new Set([room.image, ...images].filter(Boolean))];

    const user = JSON.parse(localStorage.getItem("user") || "null");

    // ============================================================
    // PRICING
    // ============================================================

    const getNights = () => {
        if (!checkIn || !checkOut) return 1;

        const start = new Date(checkIn);
        const end = new Date(checkOut);

        const diffTime = end - start;

        const nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return nights > 0 ? nights : 1;
    };

    const getTourismFee = () => {
        return Number(room?.hotel?.rating) === 5 ? 20 : 10;
    };

    const activeSeason = room?.seasonal_prices?.find((season) => {
        const date = new Date(selectedDate);

        return (
            date >= new Date(season.start_date) && date <= new Date(season.end_date)
        );
    });

    const nightlyRate = (() => {
        if (!room) return 0;

        let finalPrice = Number(room.price || 0);

        if (room.seasonal_prices?.length > 0) {
            const date = new Date(selectedDate);

            const match = room.seasonal_prices.find((season) => {
                const start = new Date(season.start_date);
                const end = new Date(season.end_date);

                return date >= start && date <= end;
            });

            if (match) {
                finalPrice = Number(match.price || finalPrice);
            }
        }

        if (room.discount_price && Number(room.discount_price) < finalPrice) {
            finalPrice = Number(room.discount_price);
        }

        return finalPrice;
    })();

    const getFinalPriceBreakdown = () => {
        const base = nightlyRate;
        const nights = getNights();

        const serviceCharge = 0.1;
        const vat = 0.05;
        const tourismFee = getTourismFee();

        const nightlyWithTax =
            base + base * serviceCharge + base * vat + tourismFee;

        return {
            base,
            nights,
            serviceCharge,
            vat,
            tourismFee,
            nightlyWithTax,
            total: Math.round(nightlyWithTax * nights),
        };
    };

    const roundPrice = (value) => Math.round(Number(value || 0));

    const isDiscounted =
        room?.discount_price && Number(room.discount_price) < Number(room.price);

    const savedAmount = isDiscounted
        ? Number(room.price) - Number(room.discount_price)
        : 0;

    const discountPercent = isDiscounted
        ? Math.round(
            ((Number(room.price) - Number(room.discount_price)) /
                Number(room.price)) *
            100,
        )
        : 0;

    const isSeasonal = Boolean(activeSeason?.price);

    // ============================================================
    // GALLERY
    // ============================================================

    const openGallery = (index) => {
        setModalIndex(index);
        setActiveImage(allImages[index]);
        setShowModal(true);
    };

    const nextImage = () => {
        if (!allImages.length) return;

        setModalIndex((prev) => {
            const nextIndex = prev === allImages.length - 1 ? 0 : prev + 1;

            setActiveImage(allImages[nextIndex]);

            return nextIndex;
        });
    };

    const previousImage = () => {
        if (!allImages.length) return;

        setModalIndex((prev) => {
            const previousIndex = prev === 0 ? allImages.length - 1 : prev - 1;

            setActiveImage(allImages[previousIndex]);

            return previousIndex;
        });
    };

    // ============================================================
    // BOOKING
    // ============================================================

    const handleBooking = async () => {
        if (!name || !email || !phone || !checkIn || !checkOut) {
            alert("Please fill all fields");
            return;
        }

        if (new Date(checkOut) <= new Date(checkIn)) {
            alert("Check-out must be after Check-in");
            return;
        }

        if (!executeRecaptcha) {
            alert("Captcha not ready. Refresh page.");
            return;
        }

        try {
            setBookingLoading(true);

            const token = await executeRecaptcha("room_booking");

            const breakdown = getFinalPriceBreakdown();

            const bookingData = {
                user_id: user?.id,
                hotel_id: room?.hotel?.id ?? room?.hotel_id,
                room_id: room.id,
                room_name: room.name || `Room ${room.room_number}`,
                room_price: room.price,
                discount_price: room.discount_price || null,
                name,
                email,
                phone,
                check_in: checkIn,
                check_out: checkOut,
                guests,
                season_name: activeSeason?.season_name || null,
                season_start: activeSeason?.start_date || null,
                season_end: activeSeason?.end_date || null,
                total_amount: breakdown.total,
                nights: breakdown.nights,
                captcha: token,
            };

            const res = await axios.post(
                "https://cheapdubaihotels.com/api/hotel-payment",
                bookingData,
            );

            if (res.data.payment_url) {
                window.location.href = res.data.payment_url;
            } else {
                alert("Payment URL not received");
            }
        } catch (err) {
            console.error(err);

            if (err.response) {
                alert(
                    `❌ ${err.response.data.error ||
                    err.response.data.message ||
                    "Booking failed"
                    }`,
                );
            } else {
                alert("❌ Server error");
            }
        } finally {
            setBookingLoading(false);
        }
    };

    const finalNightly = roundPrice(getFinalPriceBreakdown().nightlyWithTax);

    const stayTotal = roundPrice(getFinalPriceBreakdown().total);

    // ============================================================
    // RETURN
    // ============================================================

    return (
        <>
            <Helmet>
                <title>{room.meta_title || room.name}</title>

                <meta
                    name="description"
                    content={room.meta_description || room.description}
                />

                <link
                    rel="canonical"
                    href={`https://cheapdubaihotels.com/hotels/${room.hotel?.slug}/${room.id}`}
                />

                <meta property="og:title" content={room.meta_title || room.name} />

                <meta
                    property="og:description"
                    content={room.meta_description || room.description}
                />

                <meta
                    property="og:url"
                    content={`https://cheapdubaihotels.com/hotels/${room.hotel?.slug}/${room.id}`}
                />

                <meta
                    property="og:image"
                    content={
                        room?.image
                            ? `${ROOM_IMG}/${room.image}`
                            : "https://cheapdubaihotels.com/images/default-og.png"
                    }
                />

                <meta property="og:type" content="website" />

                <meta name="twitter:card" content="summary_large_image" />

                <meta name="twitter:title" content={room.meta_title || room.name} />

                <meta
                    name="twitter:description"
                    content={room.meta_description || room.description}
                />

                <meta
                    name="twitter:image"
                    content={
                        room?.image
                            ? `${ROOM_IMG}/${room.image}`
                            : "https://cheapdubaihotels.com/images/default-og.png"
                    }
                />

                <meta name="robots" content="index, follow" />
            </Helmet>

            <main className="min-h-screen bg-[#F3F7FA] text-[#002B45] pb-24 lg:pb-0 mt-28">
                {/* PAGE INTRO — compact editorial header instead of full-screen image overlay */}
                <section className="border-b border-[#0B6E8E]/10 bg-white">
                    <div className="mx-auto max-w-[1536px]
              px-4
            sm:px-6
            lg:px-8 py-7">
                        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-2 text-xs font-semibold text-slate-500 sm:text-sm">
                                <button
                                    onClick={() => navigate("/")}
                                    className="transition hover:text-[#FF5A1F]"
                                >
                                    Home
                                </button>
                                <FaChevronRight className="shrink-0 text-[9px] text-slate-300" />
                                <button
                                    onClick={() =>
                                        room.hotel?.slug && navigate(`/hotels/${room.hotel.slug}`)
                                    }
                                    className="max-w-[180px] truncate transition hover:text-[#FF5A1F] sm:max-w-xs"
                                >
                                    {room.hotel?.name || "Hotel"}
                                </button>
                                <FaChevronRight className="shrink-0 text-[9px] text-slate-300" />
                                <span className="truncate text-[#002B45]">
                                    Room {room.room_number}
                                </span>
                            </div>
                            <button
                                onClick={() => navigate(-1)}
                                className="inline-flex items-center gap-2 rounded-full border border-[#0B6E8E]/20 bg-white px-4 py-2 text-xs font-bold text-[#002B45] transition hover:border-[#FF5A1F] hover:text-[#FF5A1F] sm:text-sm"
                            >
                                <FaArrowLeft /> Back
                            </button>
                        </div>

                        <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">
                            <div className="max-w-3xl">
                                <div className="mb-3 flex flex-wrap items-center gap-2">
                                    <span className="rounded-full bg-[#E8F4F7] px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#0B6E8E]">
                                        Room {room.room_number}
                                    </span>
                                    {room.hotel?.rating && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-[#F7B928]/25 px-3 py-1.5 text-xs font-extrabold text-[#002B45]">
                                            <span className="text-[#FF5A1F]">★</span>{" "}
                                            {room.hotel.rating} Star Hotel
                                        </span>
                                    )}
                                    {isSeasonal && (
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FF5A1F]/10 px-3 py-1.5 text-xs font-bold text-[#FF5A1F]">
                                            <FaFire /> Limited-time offer
                                        </span>
                                    )}
                                    {isDiscounted && (
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0B6E8E] px-3 py-1.5 text-xs font-bold text-white">
                                            <FaTag /> {discountPercent}% OFF
                                        </span>
                                    )}
                                </div>
                                <h1 className="text-3xl font-black leading-tight tracking-[-0.04em] text-[#002B45] sm:text-4xl lg:text-5xl">
                                    {room.name || `Room ${room.room_number}`}
                                </h1>
                                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
                                    <span className="inline-flex items-center gap-2">
                                        <FaMapMarkerAlt className="text-[#FF5A1F]" />
                                        {room.hotel?.name || "Dubai Hotel"}
                                    </span>
                                    <span className="inline-flex items-center gap-2">
                                        <FaUsers className="text-[#0B6E8E]" />
                                        Up to {room.capacity || "-"} guests
                                    </span>
                                    <span className="inline-flex items-center gap-2">
                                        <FaBed className="text-[#0B6E8E]" />
                                        {room.bed_type || "Comfortable bedding"}
                                    </span>
                                    {room.size_sqm && (
                                        <span className="inline-flex items-center gap-2">
                                            <FaExpand className="text-[#0B6E8E]" />
                                            {room.size_sqm} m²
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center justify-between gap-5 rounded-2xl border border-[#0B6E8E]/15 bg-[#F3F7FA] p-4 md:min-w-[230px] md:justify-end md:p-5">
                                <div className="md:text-right">
                                    <p className="text-xs font-semibold text-slate-500">
                                        Price per night
                                    </p>
                                    <p className="mt-0.5 text-2xl font-black tracking-tight text-[#002B45]">
                                        {finalNightly.toLocaleString()}{" "}
                                        <span className="text-sm font-bold">AED</span>
                                    </p>
                                    <p className="text-[11px] text-slate-500">
                                        Including applicable fees
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowBookingModal(true)}
                                    disabled={room.is_available === false}
                                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-[#FF5A1F] px-4 py-3 text-sm font-extrabold text-white shadow-lg shadow-[#FF5A1F]/20 transition hover:-translate-y-0.5 hover:bg-[#e94a12] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <FaCalendarAlt /> Book
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* IMAGE GALLERY — asymmetrical gallery with visible thumbnails */}
                <section className="mx-auto max-w-[1440px] px-5 pt-6 sm:px-8 sm:pt-8 lg:px-12 xl:px-16">
                    <div className="grid gap-3 overflow-hidden rounded-[24px] sm:rounded-[30px] md:grid-cols-[minmax(0,1.7fr)_minmax(240px,0.8fr)]">
                        <button
                            type="button"
                            onClick={() =>
                                openGallery(Math.max(0, allImages.indexOf(activeImage)))
                            }
                            className="group relative min-h-[260px] overflow-hidden bg-[#002B45] text-left sm:min-h-[360px] lg:min-h-[470px]"
                            aria-label="Open room photo gallery"
                        >
                            {activeImage ? (
                                <img
                                    src={`${ROOM_IMG}/${activeImage}`}
                                    alt={room.name || "Room"}
                                    className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-[1.025]"
                                />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center text-white/70">
                                    <FaBed className="text-5xl" />
                                </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#002B45]/65 via-transparent to-[#002B45]/10" />
                            <span className="absolute bottom-5 left-5 inline-flex items-center gap-2 rounded-full border border-white/30 bg-[#002B45]/55 px-4 py-2.5 text-xs font-bold text-white backdrop-blur-md sm:bottom-6 sm:left-6 sm:text-sm">
                                <FaExpand /> View photos{" "}
                                {allImages.length > 0 ? `· ${allImages.length} images` : ""}
                            </span>
                            {allImages.length > 1 && (
                                <>
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            previousImage();
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.stopPropagation();
                                                previousImage();
                                            }
                                        }}
                                        className="absolute right-[68px] top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-[#002B45]/45 text-white backdrop-blur-md transition hover:bg-[#FF5A1F]"
                                        aria-label="Previous image"
                                    >
                                        <FaArrowLeft />
                                    </span>
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            nextImage();
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.stopPropagation();
                                                nextImage();
                                            }
                                        }}
                                        className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-[#002B45]/45 text-white backdrop-blur-md transition hover:bg-[#FF5A1F]"
                                        aria-label="Next image"
                                    >
                                        <FaArrowRight />
                                    </span>
                                </>
                            )}
                        </button>

                        <div className="grid grid-cols-2 gap-3 md:grid-cols-1 md:grid-rows-2">
                            {allImages.slice(0, 2).map((img, index) => (
                                <button
                                    key={`${img}-${index}`}
                                    onClick={() => openGallery(index)}
                                    className="group relative min-h-[125px] overflow-hidden bg-[#DCEAF0] text-left sm:min-h-[170px] md:min-h-0"
                                    aria-label={`View room photo ${index + 1}`}
                                >
                                    <img
                                        src={`${ROOM_IMG}/${img}`}
                                        alt={`${room.name || "Room"} photo ${index + 1}`}
                                        className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#002B45]/40 to-transparent opacity-70 transition group-hover:opacity-100" />
                                    {index === 1 && allImages.length > 2 && (
                                        <span className="absolute inset-0 flex items-center justify-center bg-[#002B45]/55 text-lg font-extrabold text-white backdrop-blur-[2px]">
                                            +{allImages.length - 2} photos
                                        </span>
                                    )}
                                </button>
                            ))}
                            {allImages.length === 0 && (
                                <div className="col-span-2 flex min-h-[125px] items-center justify-center rounded-2xl bg-[#E8F4F7] text-sm font-semibold text-[#0B6E8E] md:col-span-1">
                                    Room photos coming soon
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* =========================================================
                    QUICK ROOM INFO
                ========================================================== */}

                <section className="relative z-20 pt-6 md:pt-8">
                    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">
                        <div className="grid overflow-hidden rounded-2xl border border-[#0B6E8E]/15 bg-white shadow-[0_10px_35px_rgba(0,43,69,0.07)] sm:grid-cols-2 lg:grid-cols-4">
                            {/* Guests */}

                            <div className="flex items-center gap-4 border-b border-[#0B6E8E]/10 p-5 sm:border-r lg:border-b-0">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#E8F4F7] text-[#FF5A1F]">
                                    <FaUsers />
                                </div>

                                <div>
                                    <p className="text-xs font-medium text-slate-400">Guests</p>

                                    <p className="mt-1 font-bold text-[#002B45]">
                                        {room.capacity || "-"} Guests
                                    </p>
                                </div>
                            </div>

                            {/* Bed */}

                            <div className="flex items-center gap-4 border-b border-[#0B6E8E]/10 p-5 lg:border-b-0 lg:border-r">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#E8F4F7] text-[#FF5A1F]">
                                    <FaBed />
                                </div>

                                <div>
                                    <p className="text-xs font-medium text-slate-400">Bed Type</p>

                                    <p className="mt-1 font-bold text-[#002B45]">
                                        {room.bed_type || "-"}
                                    </p>
                                </div>
                            </div>

                            {/* Size */}

                            <div className="flex items-center gap-4 border-b border-[#0B6E8E]/10 p-5 sm:border-r sm:border-b-0">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#FFF3D3] text-[#0B6E8E]">
                                    <FaExpand />
                                </div>

                                <div>
                                    <p className="text-xs font-medium text-slate-400">
                                        Room Size
                                    </p>

                                    <p className="mt-1 font-bold text-[#002B45]">
                                        {room.size_sqm ? `${room.size_sqm} m²` : "-"}
                                    </p>
                                </div>
                            </div>

                            {/* Availability */}

                            <div className="flex items-center gap-4 p-5">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#E8F4F7] text-[#FF5A1F]">
                                    <FaShieldAlt />
                                </div>

                                <div>
                                    <p className="text-xs font-medium text-slate-400">
                                        Availability
                                    </p>

                                    <p
                                        className={`mt-1 font-bold ${room.is_available === false
                                            ? "text-red-500"
                                            : "text-[#0B755F]"
                                            }`}
                                    >
                                        {room.is_available === false
                                            ? "Unavailable"
                                            : "Available to Book"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* =========================================================
                    MAIN CONTENT
                ========================================================== */}

                <section className="mx-auto mx-auto max-w-[1536px]
              px-4
            sm:px-6
            lg:px-8 pt-8 pb-16">
                    <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_390px]">
                        {/* =================================================
                            LEFT CONTENT
                        ================================================== */}

                        <div className="space-y-7">
                            {/* ROOM OVERVIEW */}
                            <div className="rounded-2xl border border-[#0B6E8E]/12 bg-white p-6 shadow-[0_8px_28px_rgba(0,43,69,0.06)] sm:p-8">
                                <div className="flex items-start gap-4">
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#E8F4F7] text-[#0B6E8E]">
                                        <FaHome className="text-lg" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#FF5A1F]">
                                            Your stay
                                        </p>
                                        <h2 className="mt-1 text-2xl font-black tracking-tight text-[#002B45] sm:text-3xl">
                                            About this room
                                        </h2>
                                    </div>
                                </div>
                                <div
                                    className="room-content prose prose-slate mt-5 max-w-none text-sm leading-7"
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            room.description ||
                                            "<p>Enjoy a comfortable stay with modern facilities and convenient access to Dubai.</p>",
                                    }}
                                />
                            </div>

                            {/* =================================================
                                AMENITIES
                            ================================================== */}

                            <div className="rounded-2xl border border-[#0B6E8E]/12 bg-white p-6 shadow-[0_8px_28px_rgba(0,43,69,0.06)] sm:p-8">
                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF5A1F]">
                                    Comfort & convenience
                                </p>

                                <h2 className="mt-1 text-2xl font-extrabold text-[#002B45]">
                                    Room Amenities
                                </h2>

                                {amenities.length > 0 ? (
                                    <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        {amenities.map((amenity, index) => (
                                            <div
                                                key={`${amenity}-${index}`}
                                                className="flex items-center gap-3 rounded-2xl border border-[#0B6E8E]/10 bg-[#F3F7FA] px-4 py-3.5"
                                            >
                                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E8F4F7] text-[#FF5A1F]">
                                                    <FaCheck className="text-xs" />
                                                </span>

                                                <span className="text-sm font-medium text-slate-700">
                                                    {amenity}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="mt-5 text-sm text-slate-400">
                                        No amenities listed for this room.
                                    </p>
                                )}
                            </div>

                            {/* =================================================
                                POLICIES
                            ================================================== */}

                            <div className="rounded-2xl border border-[#0B6E8E]/12 bg-white p-6 shadow-[0_8px_28px_rgba(0,43,69,0.06)] sm:p-8">
                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF5A1F]">
                                    Before you book
                                </p>

                                <h2 className="mt-1 text-2xl font-extrabold text-[#002B45]">
                                    Room Policies
                                </h2>

                                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                    <div className="rounded-2xl bg-[#F3F7FA] p-4">
                                        <p className="text-xs font-medium text-slate-400">
                                            Cancellation
                                        </p>

                                        <p className="mt-1 text-sm font-semibold text-slate-700">
                                            {room.cancellation_policy || "Policy applies"}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl bg-[#F3F7FA] p-4">
                                        <p className="text-xs font-medium text-slate-400">
                                            Breakfast
                                        </p>

                                        <p className="mt-1 text-sm font-semibold text-slate-700">
                                            {room.breakfast_included ? "Included" : "Not Included"}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl bg-[#F3F7FA] p-4">
                                        <p className="text-xs font-medium text-slate-400">
                                            Smoking
                                        </p>

                                        <p className="mt-1 text-sm font-semibold text-slate-700">
                                            {room.smoking_allowed ? "Allowed" : "Not Allowed"}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl bg-[#F3F7FA] p-4">
                                        <p className="text-xs font-medium text-slate-400">
                                            Availability
                                        </p>

                                        <p
                                            className={`mt-1 text-sm font-semibold ${room.is_available === false
                                                ? "text-red-500"
                                                : "text-[#0B755F]"
                                                }`}
                                        >
                                            {room.is_available === false
                                                ? "Unavailable"
                                                : "Available"}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* =================================================
                                WHY THIS ROOM
                            ================================================== */}

                            <div className="relative overflow-hidden rounded-[28px] bg-[#002B45] p-7 text-white shadow-[0_15px_45px_rgba(0,43,69,0.12)] sm:p-9">
                                <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-white/5" />

                                <div className="relative max-w-2xl">
                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#F7B928]">
                                        A better stay
                                    </p>

                                    <h2 className="!mt-2 !text-white text-2xl font-extrabold sm:text-3xl">
                                        Everything you need for a comfortable Dubai stay.
                                    </h2>

                                    <p className="mt-3 text-sm leading-6 text-white/70">
                                        Enjoy a carefully selected room with comfortable
                                        accommodation, convenient facilities and transparent
                                        pricing.
                                    </p>

                                    <div className="mt-6 grid gap-3 sm:grid-cols-3">
                                        {[
                                            "Great location",
                                            "Transparent pricing",
                                            "Booking support",
                                        ].map((item) => (
                                            <div
                                                key={item}
                                                className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-xs font-semibold"
                                            >
                                                <FaCheck className="text-[#F7B928]" />

                                                {item}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* =================================================
                            DESKTOP BOOKING CARD
                        ================================================== */}

                        <aside className="lg:sticky lg:top-24">
                            <div className="overflow-hidden rounded-[24px] border border-[#0B6E8E]/10 bg-white shadow-[0_16px_50px_rgba(0,43,69,0.10)]">
                                {/* Header */}
                                <div className="relative overflow-hidden bg-[#002B45] px-6 py-7 text-white">
                                    <div className="absolute -right-10 -top-12 h-36 w-36 rounded-full bg-white/[0.04]" />
                                    <div className="absolute -bottom-16 right-16 h-36 w-36 rounded-full bg-white/[0.04]" />

                                    <div className="relative flex items-start justify-between gap-4">
                                        <div>
                                            <div className="mb-3 flex items-center gap-2">
                                                <span className="h-1.5 w-1.5 rounded-full bg-[#F7B928]" />
                                                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60">
                                                    Room Reservation
                                                </p>
                                            </div>

                                            <h3 className="!mt-0 !text-white text-xl font-bold leading-snug">
                                                Your Dubai stay starts here
                                            </h3>

                                            <p className="mt-1.5 text-xs text-white/55">
                                                Comfort, convenience and a memorable stay.
                                            </p>
                                        </div>

                                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-[#F7B928]">
                                            <FaShieldAlt size={18} />
                                        </div>
                                    </div>

                                    <div className="relative mt-6 flex items-end gap-2">
                                        <span className="text-4xl font-extrabold tracking-tight">
                                            {finalNightly.toLocaleString()}
                                        </span>

                                        <span className="mb-1.5 text-xs font-medium text-white/60">
                                            AED / night
                                        </span>
                                    </div>

                                    <div className="relative mt-3 flex items-center gap-2 text-[11px] text-white/60">
                                        <FaCheckCircle className="text-[#F7B928]" />
                                        <span>Transparent pricing for your stay</span>
                                    </div>
                                </div>

                                <div className="p-5 sm:p-6">
                                    {/* Discount */}
                                    {isDiscounted && (
                                        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-emerald-100 bg-emerald-50/80 px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                                                    <FaTag size={13} />
                                                </div>

                                                <div>
                                                    <p className="text-sm font-extrabold text-emerald-800">
                                                        {discountPercent}% OFF
                                                    </p>
                                                    <p className="text-[10px] text-emerald-700/70">
                                                        Special room discount
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="text-right">
                                                <p className="text-[10px] font-medium text-emerald-700/70">
                                                    You save
                                                </p>
                                                <p className="text-sm font-bold text-emerald-800">
                                                    {savedAmount.toLocaleString()} AED
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Seasonal */}
                                    {isSeasonal && (
                                        <div className="mb-4 flex gap-3 rounded-xl border border-orange-100 bg-[#FFF8F2] p-4">
                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-[#B85C2B]">
                                                <FaFire size={15} />
                                            </div>

                                            <div>
                                                <p className="text-sm font-bold text-[#A94F27]">
                                                    Seasonal offer active
                                                </p>

                                                <p className="mt-1 text-xs leading-5 text-[#A94F27]/75">
                                                    Dynamic pricing may change based on demand and
                                                    availability.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Divider */}
                                    <div className="my-5 border-t border-dashed border-slate-200" />

                                    {/* Price Breakdown */}
                                    <div>
                                        <div className="mb-4 flex items-center justify-between">
                                            <h4 className="text-sm font-bold text-[#002B45]">
                                                Price breakdown
                                            </h4>

                                            <span className="rounded-full bg-[#F4F7F5] px-2.5 py-1 text-[10px] font-semibold text-[#0B6E8E]">
                                                Per night
                                            </span>
                                        </div>

                                        <div className="space-y-4 text-[13px]">
                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-slate-500">
                                                    Room price / night
                                                </span>

                                                <span className="font-semibold text-slate-700">
                                                    {nightlyRate.toLocaleString()} AED
                                                </span>
                                            </div>

                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-slate-500">
                                                    Service charge (10%)
                                                </span>

                                                <span className="font-medium text-slate-700">
                                                    {roundPrice(nightlyRate * 0.1).toLocaleString()} AED
                                                </span>
                                            </div>

                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-slate-500">VAT (5%)</span>

                                                <span className="font-medium text-slate-700">
                                                    {roundPrice(nightlyRate * 0.05).toLocaleString()} AED
                                                </span>
                                            </div>

                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-slate-500">
                                                    Tourism fee
                                                    {Number(room?.hotel?.rating) === 5 && " (5-Star)"}
                                                </span>

                                                <span className="font-medium text-slate-700">
                                                    {getTourismFee()} AED
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Final Price */}
                                    <div className="mt-6 rounded-2xl border border-[#0B6E8E]/10 bg-[#F2F7F5] p-4">
                                        <div className="flex items-center justify-between gap-3">
                                            <div>
                                                <p className="text-xs font-semibold text-slate-500">
                                                    Final price / night
                                                </p>

                                                <p className="mt-1 text-[10px] text-slate-400">
                                                    Including applicable charges
                                                </p>
                                            </div>

                                            <div className="text-right">
                                                <p className="text-xl font-extrabold tracking-tight text-[#0B6E8E]">
                                                    {finalNightly.toLocaleString()} AED
                                                </p>
                                            </div>
                                        </div>

                                        {checkIn && checkOut && (
                                            <div className="mt-4 flex items-center justify-between border-t border-[#0B6E8E]/10 pt-4">
                                                <div>
                                                    <p className="text-xs font-semibold text-[#002B45]">
                                                        Total stay
                                                    </p>

                                                    <p className="mt-1 text-[10px] text-slate-500">
                                                        {getNights()} night(s)
                                                    </p>
                                                </div>

                                                <p className="text-lg font-extrabold text-[#0B6E8E]">
                                                    {stayTotal.toLocaleString()} AED
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Reserve Button */}
                                    <button
                                        onClick={() => setShowBookingModal(true)}
                                        disabled={room.is_available === false}
                                        className={`mt-5 flex w-full items-center justify-center gap-2 rounded-xl py-4 text-sm font-bold transition-all duration-300 ${room.is_available === false
                                            ? "cursor-not-allowed bg-slate-200 text-slate-400 shadow-none"
                                            : "bg-[#0B6E8E] text-white shadow-[0_8px_20px_rgba(11,85,82,0.20)] hover:-translate-y-0.5 hover:bg-[#002B45] hover:shadow-[0_12px_26px_rgba(11,85,82,0.25)]"
                                            }`}
                                    >
                                        {room.is_available === false
                                            ? "Room Unavailable"
                                            : "Reserve This Room"}

                                        {room.is_available !== false && <FaArrowRight size={13} />}
                                    </button>

                                    {/* Security Note */}
                                    <div className="mt-5 flex items-center justify-center gap-2 text-center text-[10px] leading-5 text-slate-400">
                                        <FaShieldAlt className="shrink-0 text-[#0B755F]" />

                                        <span>
                                            Secure booking • No charge until checkout/payment
                                        </span>
                                    </div>

                                    {/* Cancellation Policy */}
                                    <div className="mt-3 flex items-center justify-center gap-2 border-t border-slate-100 pt-3">
                                        <FaCheckCircle
                                            className={
                                                room.cancellation_policy === "free"
                                                    ? "text-emerald-600"
                                                    : "text-slate-400"
                                            }
                                            size={12}
                                        />

                                        <p className="text-center text-[11px] font-medium text-slate-500">
                                            {room.cancellation_policy === "free"
                                                ? "Free cancellation available"
                                                : "Cancellation policy applies"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </section>

                {/* =========================================================
                    MOBILE BOOKING BAR
                ========================================================== */}

                <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#0B6E8E]/10 bg-white/95 p-3 shadow-[0_-8px_30px_rgba(0,43,69,0.12)] backdrop-blur-md lg:hidden">
                    <div className="mx-auto flex max-w-[1440px] items-center gap-3">
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-medium text-slate-400">
                                From
                            </p>

                            <p className="text-lg font-extrabold text-[#FF5A1F]">
                                {finalNightly.toLocaleString()} AED
                                <span className="ml-1 text-[10px] font-medium text-slate-400">
                                    / night
                                </span>
                            </p>
                        </div>

                        <button
                            onClick={() => setShowBookingModal(true)}
                            disabled={room.is_available === false}
                            className="rounded-xl bg-[#0B6E8E] px-5 py-3 text-xs font-bold text-white shadow-lg transition hover:bg-[#002B45] disabled:bg-slate-300"
                        >
                            {room.is_available === false ? "Unavailable" : "Reserve Now"}
                        </button>
                    </div>
                </div>
            </main>

            {/* =============================================================
                FULLSCREEN GALLERY MODAL
            ============================================================== */}

            {showModal && allImages.length > 0 && (
                <div className="fixed inset-0 z-[100] bg-[#061F1E]/95">
                    <button
                        onClick={() => setShowModal(false)}
                        className="absolute right-10 top-40 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                        aria-label="Close gallery"
                    >
                        <FaTimes />
                    </button>

                    {allImages.length > 1 && (
                        <>
                            <button
                                onClick={previousImage}
                                className="absolute left-4 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition hover:bg-white/20 sm:left-8"
                            >
                                <FaArrowLeft />
                            </button>

                            <button
                                onClick={nextImage}
                                className="absolute right-4 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition hover:bg-white/20 sm:right-8"
                            >
                                <FaArrowRight />
                            </button>
                        </>
                    )}

                    <div className="flex h-full items-center justify-center p-6 sm:p-12">
                        <img
                            src={`${ROOM_IMG}/${allImages[modalIndex]}`}
                            alt={room.name || "Room"}
                            className="max-h-[82vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
                        />
                    </div>

                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-white backdrop-blur-md">
                        {modalIndex + 1} / {allImages.length}
                    </div>
                </div>
            )}

            {/* =============================================================
                BOOKING MODAL
            ============================================================== */}

            {showBookingModal && (
                <div className="fixed inset-0 z-[9999] overflow-y-auto bg-[#061F1E]/75 px-3 py-5 backdrop-blur-md sm:px-5 sm:py-8">
                    <div className="flex min-h-full items-center justify-center">
                        <div className="relative my-auto w-full max-w-2xl overflow-hidden rounded-[24px] border border-white/20 bg-white shadow-[0_25px_80px_rgba(0,0,0,0.25)] sm:rounded-[28px]">
                            {/* Close Button */}
                            <button
                                onClick={() => setShowBookingModal(false)}
                                aria-label="Close booking modal"
                                className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:rotate-90 hover:bg-white/20 sm:right-6 sm:top-6"
                            >
                                <FaTimes size={15} />
                            </button>

                            {/* Modal Header */}
                            <div className="relative overflow-hidden bg-[#002B45] px-5 py-7 text-white sm:px-8 sm:py-8">
                                <div className="absolute -right-12 -top-16 h-48 w-48 rounded-full bg-white/[0.035]" />
                                <div className="absolute -bottom-24 right-24 h-48 w-48 rounded-full bg-white/[0.035]" />

                                <div className="relative">
                                    <div className="mb-4 flex items-center gap-2">
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#F7B928]" />

                                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60">
                                            Complete your reservation
                                        </p>
                                    </div>

                                    <h3 className="!mt-0 !text-white pr-12 text-2xl font-extrabold leading-tight sm:text-3xl">
                                        Book {room.name || `Room ${room.room_number}`}
                                    </h3>

                                    <p className="mt-2 max-w-md text-xs leading-5 text-white/60">
                                        Enter your details below to continue with your hotel
                                        reservation.
                                    </p>

                                    <div className="mt-5 flex flex-wrap items-center gap-3">
                                        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2">
                                            <FaShieldAlt className="text-[#F7B928]" size={12} />

                                            <span className="text-[10px] font-semibold text-white/80">
                                                Secure reservation
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2">
                                            <FaCheckCircle className="text-[#F7B928]" size={12} />

                                            <span className="text-[10px] font-semibold text-white/80">
                                                Transparent pricing
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="max-h-[calc(100vh-190px)] overflow-y-auto p-5 sm:p-8">
                                {/* Guest Details */}
                                <div className="mb-5">
                                    <div className="mb-4 flex items-center gap-3">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#E8F4F7] text-[#0B6E8E]">
                                            <FaUser size={14} />
                                        </div>

                                        <div>
                                            <h4 className="text-sm font-bold text-[#002B45]">
                                                Guest Information
                                            </h4>

                                            <p className="mt-0.5 text-[11px] text-slate-400">
                                                Please provide your contact details.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid gap-4 sm:grid-cols-2">
                                        {/* Name */}
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold text-[#002B45]">
                                                Full Name
                                            </label>

                                            <input
                                                type="text"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                placeholder="Enter your full name"
                                                autoComplete="name"
                                                className="h-12 w-full rounded-xl border border-slate-200 bg-[#F8FAF8] px-4 text-sm text-[#002B45] outline-none transition placeholder:text-slate-400 hover:border-[#0B6E8E]/30 focus:border-[#0B6E8E] focus:bg-white focus:ring-4 focus:ring-[#0B6E8E]/[0.07]"
                                            />
                                        </div>

                                        {/* Email */}
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold text-[#002B45]">
                                                Email Address
                                            </label>

                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="you@example.com"
                                                autoComplete="email"
                                                className="h-12 w-full rounded-xl border border-slate-200 bg-[#F8FAF8] px-4 text-sm text-[#002B45] outline-none transition placeholder:text-slate-400 hover:border-[#0B6E8E]/30 focus:border-[#0B6E8E] focus:bg-white focus:ring-4 focus:ring-[#0B6E8E]/[0.07]"
                                            />
                                        </div>

                                        {/* Phone */}
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold text-[#002B45]">
                                                Phone Number
                                            </label>

                                            <input
                                                type="tel"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                placeholder="+971 50 000 0000"
                                                autoComplete="tel"
                                                className="h-12 w-full rounded-xl border border-slate-200 bg-[#F8FAF8] px-4 text-sm text-[#002B45] outline-none transition placeholder:text-slate-400 hover:border-[#0B6E8E]/30 focus:border-[#0B6E8E] focus:bg-white focus:ring-4 focus:ring-[#0B6E8E]/[0.07]"
                                            />
                                        </div>

                                        {/* Guests */}
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold text-[#002B45]">
                                                Number of Guests
                                            </label>

                                            <input
                                                type="number"
                                                min="1"
                                                max={room.capacity || undefined}
                                                value={guests}
                                                onChange={(e) => setGuests(e.target.value)}
                                                className="h-12 w-full rounded-xl border border-slate-200 bg-[#F8FAF8] px-4 text-sm text-[#002B45] outline-none transition hover:border-[#0B6E8E]/30 focus:border-[#0B6E8E] focus:bg-white focus:ring-4 focus:ring-[#0B6E8E]/[0.07]"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Stay Dates */}
                                <div className="mb-6 border-t border-slate-100 pt-5">
                                    <div className="mb-4 flex items-center gap-3">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#E8F4F7] text-[#0B6E8E]">
                                            <FaCalendarAlt size={14} />
                                        </div>

                                        <div>
                                            <h4 className="text-sm font-bold text-[#002B45]">
                                                Your Stay
                                            </h4>

                                            <p className="mt-0.5 text-[11px] text-slate-400">
                                                Select your arrival and departure dates.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid gap-4 sm:grid-cols-2">
                                        {/* Check-in */}
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold text-[#002B45]">
                                                Check-in Date
                                            </label>

                                            <input
                                                type="date"
                                                value={checkIn}
                                                min={today}
                                                onChange={(e) => {
                                                    setCheckIn(e.target.value);

                                                    if (checkOut && e.target.value > checkOut) {
                                                        setCheckOut("");
                                                    }
                                                }}
                                                className="h-12 w-full rounded-xl border border-slate-200 bg-[#F8FAF8] px-4 text-sm text-[#002B45] outline-none transition hover:border-[#0B6E8E]/30 focus:border-[#0B6E8E] focus:bg-white focus:ring-4 focus:ring-[#0B6E8E]/[0.07]"
                                            />
                                        </div>

                                        {/* Check-out */}
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold text-[#002B45]">
                                                Check-out Date
                                            </label>

                                            <input
                                                type="date"
                                                value={checkOut}
                                                min={checkIn || today}
                                                onChange={(e) => setCheckOut(e.target.value)}
                                                className="h-12 w-full rounded-xl border border-slate-200 bg-[#F8FAF8] px-4 text-sm text-[#002B45] outline-none transition hover:border-[#0B6E8E]/30 focus:border-[#0B6E8E] focus:bg-white focus:ring-4 focus:ring-[#0B6E8E]/[0.07]"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Price Summary */}
                                <div className="overflow-hidden rounded-2xl border border-[#0B6E8E]/10 bg-[#F3F7FA]">
                                    <div className="flex items-center justify-between gap-3 border-b border-[#0B6E8E]/10 px-5 py-4">
                                        <div>
                                            <h4 className="text-sm font-bold text-[#002B45]">
                                                Price Summary
                                            </h4>

                                            <p className="mt-1 text-[10px] text-slate-400">
                                                Your reservation estimate
                                            </p>
                                        </div>

                                        <span className="rounded-full bg-[#E8F4F7] px-3 py-1.5 text-[10px] font-bold text-[#0B6E8E]">
                                            {getNights()} night(s)
                                        </span>
                                    </div>

                                    <div className="space-y-3.5 p-5 text-[13px]">
                                        <div className="flex justify-between gap-3 text-slate-500">
                                            <span>Base price / night</span>

                                            <span className="font-medium text-slate-700">
                                                {nightlyRate.toLocaleString()} AED
                                            </span>
                                        </div>

                                        <div className="flex justify-between gap-3 text-slate-500">
                                            <span>VAT (5%)</span>

                                            <span className="font-medium text-slate-700">
                                                {roundPrice(nightlyRate * 0.05).toLocaleString()} AED
                                            </span>
                                        </div>

                                        <div className="flex justify-between gap-3 text-slate-500">
                                            <span>Service charge (10%)</span>

                                            <span className="font-medium text-slate-700">
                                                {roundPrice(nightlyRate * 0.1).toLocaleString()} AED
                                            </span>
                                        </div>

                                        <div className="flex justify-between gap-3 text-slate-500">
                                            <span>Tourism fee</span>

                                            <span className="font-medium text-slate-700">
                                                {getTourismFee()} AED
                                            </span>
                                        </div>

                                        <div className="border-t border-dashed border-slate-200 pt-4">
                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-xs font-semibold text-slate-500">
                                                    Final price / night
                                                </span>

                                                <span className="text-lg font-extrabold text-[#0B6E8E]">
                                                    {finalNightly.toLocaleString()} AED
                                                </span>
                                            </div>
                                        </div>

                                        {checkIn && checkOut && (
                                            <div className="mt-2 flex items-center justify-between gap-3 rounded-xl bg-[#002B45] px-4 py-4 text-white">
                                                <div>
                                                    <p className="text-xs font-semibold text-white/70">
                                                        Total stay
                                                    </p>

                                                    <p className="mt-1 text-[10px] text-white/50">
                                                        {getNights()} night(s)
                                                    </p>
                                                </div>

                                                <p className="text-xl font-extrabold">
                                                    {stayTotal.toLocaleString()} AED
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Secure Booking */}
                                <div className="mt-4 flex gap-3 rounded-xl border border-emerald-100 bg-emerald-50/70 p-4">
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                                        <FaShieldAlt size={15} />
                                    </div>

                                    <div>
                                        <p className="text-xs font-bold text-emerald-800">
                                            Secure booking
                                        </p>

                                        <p className="mt-1 text-[11px] leading-5 text-emerald-800/65">
                                            Your booking request is protected. Payment is handled
                                            securely through the booking process.
                                        </p>
                                    </div>
                                </div>

                                {/* Confirm Booking */}
                                <button
                                    onClick={handleBooking}
                                    disabled={bookingLoading || room.is_available === false}
                                    className={`mt-6 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl px-5 py-4 text-sm font-bold transition-all duration-300 ${bookingLoading || room.is_available === false
                                        ? "cursor-not-allowed bg-slate-200 text-slate-400 shadow-none"
                                        : "bg-[#0B6E8E] text-white shadow-[0_8px_22px_rgba(11,85,82,0.18)] hover:-translate-y-0.5 hover:bg-[#002B45] hover:shadow-[0_12px_28px_rgba(11,85,82,0.25)]"
                                        }`}
                                >
                                    {bookingLoading ? (
                                        <>
                                            <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            Confirm Booking
                                            <FaArrowRight size={14} />
                                        </>
                                    )}
                                </button>

                                <p className="mt-4 text-center text-[10px] leading-5 text-slate-400">
                                    By continuing, you agree to the applicable booking and
                                    cancellation policies.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
